<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">
      Manage Extra Maintenance Notes
    </h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
      :new-to="{ name: 'extra_maintenance_note.create', params: { id: 0 } }"
      new-button-text="New Extra Maintenance Note"
    >
      <template slot="middle">
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <b-table
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template
        slot="top-row"
        slot-scope="{ columns, fields }"
        v-if="filterRow"
      >
        <td :key="field.key" class="p-0" v-for="field in fields">
          <span v-if="field.key === 'note'">
            <b-form-input
              @change="filter_changed"
              class="rounded-0 px-0"
              type="search"
              v-model="list_options.qbe.note"
            ></b-form-input>
          </span>
          <span v-if="field.key === 'when'">
            <o-flatpickr-wrapper
              @change="filter_changed"
              class="rounded-0 px-0"
              v-model="list_options.qbe.when"
            ></o-flatpickr-wrapper>
          </span>
          <span v-if="field.key === 'who'">
            <o-form-select
              v-model="list_options.qbe.who"
              :options="optionauth.UserItems"
              text-field="username"
              value-field="id"
              sort-field="username"
              blank-option-text=""
              :loading="optionauth.UserWorking"
              @change="filter_changed"
              name="who"
            >
              <template v-slot:first>
                <option :value="null"></option>
              </template>
            </o-form-select>
          </span>
        </td>
      </template>
      <template slot="cell(note)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'extra_maintenance_note.edit', params: { id: item.id } }"
          :when="first_column_key === 'note'"
        >
          <default-display :value="item.note"></default-display>
        </o-link-cell>
      </template>
      <template slot="cell(when)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'extra_maintenance_note.edit', params: { id: item.id } }"
          :when="first_column_key === 'when'"
        >
          <date-display
            :value="item.when"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template slot="cell(who)" slot-scope="{ index, item }">
        <o-link-cell
          :to="{ name: 'extra_maintenance_note.edit', params: { id: item.id } }"
          :when="first_column_key === 'who'"
        >
          <lookup-display
            :list="optionauthUserItemsById"
            :value="item.who"
            display-field="username"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'extra_maintenance_note.create' }"
          >create a new item</router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "../../../vue-ocom/mixins";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "../../../vue-ocom/schema/schema_utils";
import { cleanUpModel } from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("ExtraMaintenanceNote", "extra_maintenance_note"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("ExtraMaintenanceNote"),
    CreateCodeTableFetchMixin(["User"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.ExtraMaintenanceNote,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.ExtraMaintenanceNote),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        { name: "Active", code: "active", options: { offset: 0, sort: "id" } },
        {
          name: "Non Active",
          code: "inactive",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection: null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
    };
  },
  computed: {
    options() {
      return {
        "auth.User": {
          options: this.optionauth.UserItems,
          textField: "username",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "when",
        "who",
        "note",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
  },
};
</script>
